import { useEffect, useState } from 'react';
import { useStore } from '@nanostores/react';
import type { BillingUsage } from './transforms';
import { accountStore } from '@/services/auth/store';
import { billing } from '.';

export type BillingUsageLoad = {
  usage: BillingUsage | undefined;
  /**
   * Whether the load has finished, whatever it produced. `loadUsage` catches its own failure, so
   * the promise settles either way — a caller that needs to tell a failure from a load still in
   * flight reads this alongside `usage`. Stays false while the load is disabled.
   */
  hasSettled: boolean;
};

/**
 * Lazily loads account usage through the billing service. The billing service is the canonical
 * gateway for billing data (usage included), so components read usage through this rather than
 * fetching it directly.
 *
 * `loadUsage` is enqueued, so calls from several mounted consumers run one after another rather than
 * at once; the cross-app usage cache is what keeps them to one request. Pass
 * `enabled: false` to skip the request where the usage would not be read — the load raises a
 * snackbar of its own on failure unless `silent`, so a surface that shows no usage should not start
 * it.
 */
export function useBillingUsageLoad(options?: { silent?: boolean; enabled?: boolean }): BillingUsageLoad {
  const account = useStore(accountStore);
  const accountId = account?.id;
  const silent = options?.silent ?? false;
  const enabled = options?.enabled ?? true;
  const [hasSettled, setHasSettled] = useState(false);

  useEffect(() => {
    if (accountId == null || !enabled) {
      return;
    }

    // The account can change while a load is in flight; a late resolve must not report the
    // previous account's load as this one's.
    let isCurrent = true;
    setHasSettled(false);

    void billing.loadUsage.perform({ silent }).then(() => {
      if (isCurrent) {
        setHasSettled(true);
      }
    });

    return () => {
      isCurrent = false;
    };
  }, [accountId, silent, enabled]);

  return { usage: useStore(billing.usage), hasSettled };
}

/** The usage alone, for a caller with no error state to drive. */
export function useBillingUsage(options?: { silent?: boolean }): BillingUsage | undefined {
  return useBillingUsageLoad(options).usage;
}
